import { useState, useCallback, useRef } from 'react';

export interface FileWithPreview {
  id: string;
  file: File; // Store the original File object
  name: string;
  size: number;
  type: string;
  url?: string;
  preview?: string;
}

interface UseFileUploadProps {
  maxSize?: number; // in bytes
  accept?: string;
  initialFiles?: FileWithPreview[];
}

interface UseFileUploadState {
  files: FileWithPreview[];
  isDragging: boolean;
  errors: string[];
}

interface UseFileUploadActions {
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  openFileDialog: () => void;
  removeFile: (id: string) => void;
  getInputProps: () => {
    type: string;
    accept?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    style: { display: string };
    'aria-label': string;
    ref: React.RefObject<HTMLInputElement>;
  };
}

export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const useFileUpload = ({
  maxSize = 10 * 1024 * 1024, // 10MB default
  accept = 'image/*,application/pdf',
  initialFiles = [],
}: UseFileUploadProps = {}): [UseFileUploadState, UseFileUploadActions] => {
  const [files, setFiles] = useState<FileWithPreview[]>(initialFiles);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return `File ${file.name} is too large. Max size is ${formatBytes(maxSize)}.`;
    }
    // Simple accept check (can be improved for complex mime types)
    if (accept && accept !== '*') {
      const acceptedTypes = accept.split(',').map(t => t.trim());
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      const isValid = acceptedTypes.some(type => {
        if (type.endsWith('/*')) {
          return fileType.startsWith(type.replace('/*', ''));
        }
        if (type.startsWith('.')) {
          return fileName.endsWith(type.toLowerCase());
        }
        return fileType === type;
      });

      if (!isValid) {
        return `File ${file.name} has an invalid type.`;
      }
    }
    return null;
  };

  const handleFiles = useCallback((newFiles: File[]) => {
    const newErrors: string[] = [];

    newFiles.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = e.target?.result as string;

          setFiles(() => {
            // Replace existing file to behave like single file upload
            const newFileObj: FileWithPreview = {
              id: `${file.name}-${Date.now()}`,
              file: file,
              name: file.name,
              size: file.size,
              type: file.type,
              preview: file.type.startsWith('image/') ? preview : undefined,
            };
            return [newFileObj];
          });
        };
        if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else {
          const newFileObj: FileWithPreview = {
            id: `${file.name}-${Date.now()}`,
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
          };
          setFiles([newFileObj]);
        }
      }
    });

    setErrors(newErrors);
  }, [maxSize, accept]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [handleFiles]);

  const openFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
    setErrors([]); // Clear errors when removing file
  }, []);

  const getInputProps = useCallback(
    () => ({
      type: 'file',
      accept,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (fileList && fileList.length > 0) {
          handleFiles(Array.from(fileList));
        }
        // Reset input value so same file can be selected again if needed
        e.target.value = '';
      },
      style: { display: 'none' },
      'aria-label': 'Upload file',
      ref: inputRef,
    }),
    [accept, handleFiles]
  );

  return [
    {
      files,
      isDragging,
      errors,
    },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
      getInputProps,
    },
  ];
};
